import React, { useEffect, useState } from "react";
import { webOrigin } from "@/lib/nativeBridge";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { DogIcon as Dog, StorefrontIcon as Storefront, CheckCircleIcon as CheckCircle, EnvelopeSimpleIcon as EnvelopeSimple, PaintBrushIcon as PaintBrush } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { track } from "@/lib/analytics";
import { thumbUrl } from "@/lib/utils-app";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { QuantityInput } from "@/components/QuantityInput";

const ACCENT_HEX = {
  gold: "#C9A227", ink: "#0E0E0E", crimson: "#C1272D",
  emerald: "#0E7A53", sky: "#1E6FB6", violet: "#6B3FA0",
};

/**
 * Public, multi-tenant kennel storefront at `/store/:slug`.
 *
 * Mirrors the Armstrong storefront experience but driven entirely by
 * the seller's own data (name, bio, products, delivery zone). No auth
 * required to browse or buy — Stripe Checkout handles identity
 * collection. The 5% platform fee is deducted on Stripe's side via the
 * Connect application_fee_amount, invisible to the buyer.
 */
export default function PublicStore() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get(`/store/${slug}`)
      .then(({ data }) => { if (!cancelled) setData(data); })
      .catch((err) => { if (!cancelled) setNotFound(err?.response?.status === 404); });
    // Surface the post-checkout success message when Stripe redirects back.
    if (params.get("status") === "paid") {
      toast.success("Payment received — the breeder will reach out about delivery.");
    }
    return () => { cancelled = true; };
  }, [slug, params]);

  if (notFound) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#FAFAFA] p-6 text-center">
        <Storefront size={48} weight="duotone" className="text-[#9A9A9A] mb-4" />
        <h1 className="font-spectral text-3xl text-ink mb-2">Storefront not found</h1>
        <p className="text-[#6B6B6B] mb-6">This store doesn&rsquo;t exist or has been closed.</p>
        <Link to="/" className="text-gold-dark underline">Back to home →</Link>
      </div>
    );
  }
  if (!data) {
    return <div className="min-h-screen flex items-center justify-center text-[#6B6B6B]">Loading…</div>;
  }

  const { store, products } = data;
  const food = products.filter((p) => p.kind === "food");
  const merch = products.filter((p) => p.kind === "merch");
  const accent = ACCENT_HEX[store.accent_color] || ACCENT_HEX.gold;
  const heroImg = store.hero_image ? (thumbUrl(store.hero_image) || store.hero_image) : null;

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="public-store">
      <Helmet>
        <title>{`${store.name} — Storefront`}</title>
        <meta name="description" content={store.bio || `Shop dog food and merch from ${store.name}.`} />
      </Helmet>

      <header
        className="relative text-white px-6 py-12 sm:py-16 overflow-hidden"
        style={{
          background: heroImg
            ? `linear-gradient(135deg, rgba(14,14,14,0.85), rgba(14,14,14,0.55)), url('${heroImg}') center/cover`
            : `linear-gradient(135deg, #0E0E0E, #1a1a1a)`,
        }}
      >
        <div className="max-w-5xl mx-auto relative">
          <Link to="/storefronts" className="text-[11px] hover:underline uppercase tracking-[0.2em]" style={{ color: accent }}>
            ← Back to storefronts
          </Link>
          <div className="flex items-center gap-3 mt-4">
            <Storefront size={28} weight="duotone" style={{ color: accent }} />
            <span className="text-[11px] uppercase tracking-[0.25em]" style={{ color: accent }}>Storefront</span>
            {store.is_featured && (
              <span className="text-[10px] font-bold uppercase tracking-[0.18em] rounded-full px-2 py-0.5" style={{ background: accent, color: "#0E0E0E" }}>
                ★ Featured
              </span>
            )}
          </div>
          <h1 className="font-spectral text-4xl sm:text-5xl mt-3">{store.name}</h1>
          {store.bio && (
            <p className="text-[#cfcfcf] mt-3 max-w-2xl leading-relaxed">{store.bio}</p>
          )}
          {store.contact_email && (
            <a href={`mailto:${store.contact_email}`} className="text-[12px] mt-3 inline-flex items-center gap-1 hover:underline" style={{ color: accent }}>
              <EnvelopeSimple size={12} /> {store.contact_email}
            </a>
          )}
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-10 space-y-8">
        {/* Marketplace-facilitator banner — front and center so buyers
            can't miss who they're contracting with. Mirrors §5c of the
            Terms of Service. */}
        <div
          className="rounded-xl border border-[#E8E0CC] bg-[#FBF7EC]/70 px-4 py-3 flex items-start gap-3"
          data-testid="public-store-seller-banner"
          role="note"
        >
          <Storefront size={18} weight="duotone" className="text-gold-dark mt-0.5 shrink-0" />
          <p className="text-[12.5px] text-[#3a3a3a] leading-relaxed">
            <strong className="text-ink">Sold by {store.name}</strong>
            <span className="text-[#6B6B6B]"> · Armstrong K9 Registry is not the seller.</span>
            <span className="block text-[11.5px] text-[#6B6B6B] mt-0.5">
              The Registry provides the storefront technology and payment
              plumbing only. All product, delivery, and refund questions
              go to{" "}
              {store.contact_email ? (
                <a href={`mailto:${store.contact_email}`} className="underline">{store.contact_email}</a>
              ) : "the storefront owner directly"}.
            </span>
          </p>
        </div>

        {food.length > 0 && (
          <Section title="Dog food" sub="Local delivery only — ZIP-gated at checkout.">
            <Grid>{food.map((p) => <ProductCard key={p.id} product={p} slug={slug} store={store} />)}</Grid>
          </Section>
        )}
        {merch.length > 0 && (
          <Section title="Merch" sub="Apparel and gear from the kennel.">
            <Grid>{merch.map((p) => <ProductCard key={p.id} product={p} slug={slug} store={store} />)}</Grid>
          </Section>
        )}
        {products.length === 0 && (
          <p className="text-center text-[#6B6B6B] py-12">This storefront hasn&rsquo;t added products yet — check back soon.</p>
        )}

        {/* Always-available "request something custom" CTA. Buyers can
            describe what they want even when nothing on the menu fits
            (or when the menu is empty). Goes through the same
            store_custom_orders pipeline as personalizable-product
            requests — seller approves + sets the price + the buyer
            pays via a Stripe-Connect link. */}
        <RequestCustomItemBlock slug={slug} store={store} />
      </main>

      <footer className="border-t border-[#E8E0CC] mt-6 py-8 px-6 text-[11px] text-[#6B6B6B] bg-white">
        <div className="max-w-5xl mx-auto space-y-3">
          <p className="leading-relaxed">
            <strong className="text-ink">Independent storefront.</strong> This page is operated by{" "}
            <span className="font-medium">{store.name}</span>, an independent kennel.
            Armstrong K9 Registry provides the technology and payment plumbing but is
            <strong> not the seller</strong> of the products listed here. All product, delivery,
            and refund questions go directly to{" "}
            {store.contact_email ? (
              <a href={`mailto:${store.contact_email}`} className="underline">{store.contact_email}</a>
            ) : "the storefront owner"}.
            See the{" "}
            <Link to="/terms" className="underline">Terms of Service §5b</Link>{" "}
            for full marketplace-facilitator disclosures.
          </p>
          <p>
            Payments processed by Stripe. Powered by{" "}
            <Link to="/" className="hover:text-gold-dark underline">Armstrong K9 Registry</Link>.
          </p>
        </div>
      </footer>
    </div>
  );
}

function Section({ title, sub, children }) {
  return (
    <section>
      <h2 className="font-spectral text-2xl text-ink">{title}</h2>
      {sub && <p className="text-[12.5px] text-[#6B6B6B] mt-1">{sub}</p>}
      <div className="mt-5">{children}</div>
    </section>
  );
}

function Grid({ children }) {
  return <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">{children}</div>;
}

function ProductCard({ product, slug, store }) {
  const [open, setOpen] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);
  const photoSrc = product.photo_url ? (thumbUrl(product.photo_url) || product.photo_url) : null;
  const personalizable = !!product.personalizable;
  return (
    <>
      <button
        type="button"
        onClick={() => (personalizable ? setCustomOpen(true) : setOpen(true))}
        className="text-left rounded-xl border border-[#E8E0CC] bg-white overflow-hidden hover:shadow-lg hover:border-gold-dark transition-all relative"
        data-testid={`product-${product.id}`}
      >
        {personalizable && (
          <span
            className="absolute top-2 left-2 z-10 inline-flex items-center gap-1 bg-gold-gradient text-ink text-[10px] font-bold uppercase tracking-[0.12em] rounded-full px-2 py-0.5 shadow-sm"
            data-testid={`product-personalize-badge-${product.id}`}
          >
            <PaintBrush size={10} weight="fill" /> Personalize
          </span>
        )}
        {photoSrc ? (
          <img src={photoSrc} alt={product.name} className="w-full aspect-square object-cover" />
        ) : (
          <div className="w-full aspect-square bg-cream flex items-center justify-center">
            <Dog size={48} weight="duotone" className="text-gold" />
          </div>
        )}
        <div className="p-3">
          <h3 className="font-spectral text-base leading-tight">{product.name}</h3>
          {product.bag_size && <p className="text-[11px] text-[#6B6B6B] uppercase tracking-wider mt-0.5">{product.bag_size}</p>}
          <div className="flex items-center justify-between mt-2 gap-2">
            <p className="font-tabular text-lg text-ink">${product.price_usd.toFixed(2)}{personalizable && <span className="text-[10px] text-[#9A9A9A] font-sans ml-1">starting</span>}</p>
            {personalizable && (
              <span className="text-[11px] text-gold-dark font-semibold">Request →</span>
            )}
          </div>
          <div className="mt-2 pt-2 border-t border-[#F0E9D3]">
            <p
              className="text-[10px] text-[#9A9A9A] truncate"
              data-testid={`product-seller-label-${product.id}`}
              title={`Sold by ${store.name}. Armstrong K9 Registry is not the seller.`}
            >
              Sold by <span className="font-medium text-[#6B6B6B]">{store.name}</span>
            </p>
            {product.kind === "merch" && product.printify_id && (
              <p
                className="text-[9.5px] text-[#9A9A9A] mt-0.5 truncate"
                data-testid={`product-printify-badge-${product.id}`}
                title="Printed and shipped by Printify on behalf of the seller. The Registry is not the manufacturer or shipper."
              >
                Printify-fulfilled · ships from print partner
              </p>
            )}
          </div>
        </div>
      </button>
      {!personalizable && (
        <CheckoutDialog product={product} store={store} slug={slug} open={open} onClose={() => setOpen(false)} />
      )}
      {personalizable && (
        <CustomProductRequestDialog
          product={product}
          store={store}
          slug={slug}
          open={customOpen}
          onClose={() => setCustomOpen(false)}
        />
      )}
    </>
  );
}

/**
 * Buyer-facing custom-order request form for a kennel storefront.
 * Posts to `/store/:slug/custom/request` and redirects the buyer to
 * the public `/store/:slug/custom/:orderId` status page so they can
 * track the request (and pay once the seller sends a mockup).
 *
 * No buyer login required — the unguessable order_id acts as the
 * access token, identical to the Armstrong custom-order flow.
 */
function CustomProductRequestDialog({ product, store, slug, open, onClose }) {
  const [form, setForm] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    description: "",
    quantity: 1,
    desired_by: "",
    inspiration_url: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        customer_name: "", customer_email: "", customer_phone: "",
        description: "", quantity: 1, desired_by: "", inspiration_url: "",
      });
    }
  }, [open]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if ((form.description || "").trim().length < 10) {
      toast.error("Please describe your custom design in a bit more detail.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        quantity: parseInt(form.quantity) || 1,
        customer_phone: form.customer_phone || null,
        product_id: product?.id || null,
        product_reference: product?.name || null,
        desired_by: form.desired_by || null,
        inspiration_url: form.inspiration_url || null,
      };
      const { data } = await api.post(`/store/${slug}/custom/request`, payload);
      toast.success(`Request sent — ${store.name} will email you a mockup soon.`);
      onClose();
      if (data?.id) {
        window.location.href = `/store/${slug}/custom/${data.id}`;
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Request failed — please try again.");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="custom-product-dialog">
        <DialogHeader>
          <DialogTitle className="font-spectral text-xl flex items-center gap-2">
            <PaintBrush size={20} weight="duotone" />
            {product ? `Personalize: ${product.name}` : "Request a custom item"}
          </DialogTitle>
          <DialogDescription>
            Tell {store.name} what you want. They&rsquo;ll send you a mockup and a price &mdash; you only pay once you approve.
          </DialogDescription>
        </DialogHeader>
        {product?.customization_instructions && (
          <div className="rounded-lg border border-[#E8E0CC] bg-cream/60 px-3 py-2 text-[12px] text-[#3a3a3a]" data-testid="custom-product-instructions">
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-gold-dark">Seller&rsquo;s notes</p>
            <p className="mt-0.5 whitespace-pre-wrap">{product.customization_instructions}</p>
          </div>
        )}
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label className="text-xs">Your name</Label>
            <Input required value={form.customer_name} onChange={(e) => update("customer_name", e.target.value)} data-testid="custom-product-name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Email</Label>
              <Input required type="email" value={form.customer_email} onChange={(e) => update("customer_email", e.target.value)} data-testid="custom-product-email" />
            </div>
            <div>
              <Label className="text-xs">Phone (optional)</Label>
              <Input value={form.customer_phone} onChange={(e) => update("customer_phone", e.target.value)} />
            </div>
          </div>
          <div>
            <Label className="text-xs">What do you want?</Label>
            <Textarea
              required
              rows={4}
              value={form.description}
              onChange={(e) => update("description", e.target.value)}
              placeholder="Names, colors, sizes, photos — the more specific, the better."
              data-testid="custom-product-description"
            />
            <p className="text-[10.5px] text-[#9A9A9A] mt-1">Minimum 10 characters.</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Quantity</Label>
              <QuantityInput value={form.quantity} onChange={(n) => update("quantity", n)} min={1} max={200} className="w-full" testid="store-custom-qty" />
            </div>
            <div>
              <Label className="text-xs">Need by (optional)</Label>
              <Input value={form.desired_by} onChange={(e) => update("desired_by", e.target.value)} placeholder="Dec 20" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Inspiration link (optional)</Label>
            <Input type="url" value={form.inspiration_url} onChange={(e) => update("inspiration_url", e.target.value)} placeholder="https://..." />
          </div>
          <p className="text-[11px] text-[#6B6B6B] leading-relaxed">
            No payment is collected until {store.name} sends you a mockup and price you approve.
            We&rsquo;ll redirect you to your order page so you can track progress.
          </p>
          <DialogFooter>
            <Button type="submit" disabled={busy} className="rounded-full bg-ink hover:bg-crimson text-white w-full h-11" data-testid="custom-product-submit">
              {busy ? "Sending…" : `Send request →`}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CheckoutDialog({ product, store, slug, open, onClose }) {
  const [form, setForm] = useState({
    quantity: 1, customer_name: "", customer_email: "", customer_phone: "",
    delivery_address: "", delivery_zip: "", delivery_notes: "",
    venue_acknowledged: false, direct_payment_ack: false,
  });
  const [busy, setBusy] = useState(false);
  const [zoneStatus, setZoneStatus] = useState(null); // null | "checking" | "ok" | "blocked"
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const isMerch = product.kind === "merch";
  const zoneApplies = !isMerch || !product.ships_nationwide;
  const isDirect = store.payment_model === "direct";

  useEffect(() => {
    if (!open || !zoneApplies || !store.zone_enforced) { setZoneStatus(null); return; }
    const z = (form.delivery_zip || "").trim();
    if (z.length < 5) { setZoneStatus(null); return; }
    setZoneStatus("checking");
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/store/${slug}/eligibility?zip=${encodeURIComponent(z)}&email=${encodeURIComponent(form.customer_email || "")}`);
        setZoneStatus(data.ok ? "ok" : "blocked");
      } catch { setZoneStatus(null); }
    }, 400);
    return () => clearTimeout(t);
  }, [form.delivery_zip, form.customer_email, open, store.zone_enforced, slug, zoneApplies]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/store/${slug}/checkout`, {
        product_id: product.id, ...form, origin_url: webOrigin(),
      });
      track("store_checkout_started", { store: slug });
      if (data.url) window.location.href = data.url;
    } catch (err) {
      // Friendly fallback for proxy / Cloudflare timeouts (502/504/520-524)
      // where the response body is HTML, not a JSON `detail` field.
      const detail = typeof err?.response?.data?.detail === "string"
        ? err.response.data.detail
        : null;
      const status = err?.response?.status;
      const isTransient = !err?.response || (status >= 502 && status <= 524);
      const message =
        detail
        || (isTransient
          ? "Our payment processor is slow right now. Please try again in a moment — your card has not been charged."
          : "Checkout failed. Please try again, or email the seller if it keeps happening.");
      toast.error(message);
      setBusy(false);
    }
  };

  const blocked = zoneStatus === "blocked";

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="checkout-dialog">
        <DialogHeader>
          <DialogTitle className="font-spectral text-2xl">{product.name}</DialogTitle>
          {/* div, not DialogDescription: Radix renders Description as <p>,
              and QuantityInput's wrapper <div> inside it triggers React
              nested-element hydration warnings. */}
          <div className="text-sm text-muted-foreground flex items-center flex-wrap">
            ${product.price_usd.toFixed(2)} × <QuantityInput value={form.quantity} onChange={(n) => update("quantity", n)} min={1} max={20} className="w-16 ml-1" testid="store-product-qty" />
          </div>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div><Label className="text-xs">Your name</Label><Input required value={form.customer_name} onChange={(e) => update("customer_name", e.target.value)} /></div>
          <div><Label className="text-xs">Email</Label><Input required type="email" value={form.customer_email} onChange={(e) => update("customer_email", e.target.value)} /></div>
          <div><Label className="text-xs">Phone</Label><Input required value={form.customer_phone} onChange={(e) => update("customer_phone", e.target.value)} /></div>
          <div><Label className="text-xs">{isMerch && product.ships_nationwide ? "Shipping address" : "Delivery address"}</Label><Textarea required value={form.delivery_address} onChange={(e) => update("delivery_address", e.target.value)} rows={2} placeholder="123 Main St, Anderson, CA" /></div>
          <div>
            <Label className="text-xs">ZIP code {zoneApplies && store.zone_enforced && <span className="text-[10px] text-[#9A9A9A]">(local delivery only{store.food_zone_help_text ? ` — ${store.food_zone_help_text}` : ""})</span>}</Label>
            <Input
              required
              inputMode="numeric"
              pattern="[0-9-]*"
              maxLength={10}
              value={form.delivery_zip}
              onChange={(e) => update("delivery_zip", e.target.value.replace(/[^0-9-]/g, ""))}
              placeholder="19103"
              className={zoneStatus === "blocked" ? "border-crimson focus-visible:ring-crimson" : zoneStatus === "ok" ? "border-emerald-500" : ""}
            />
            {zoneStatus === "ok" && <p className="text-[11px] text-emerald-700 mt-1">✓ In delivery zone</p>}
            {blocked && (
              <div className="mt-2 rounded-lg border border-crimson/30 bg-crimson/5 px-3 py-2 text-[12px] text-crimson leading-relaxed">
                Sorry — this store doesn&rsquo;t deliver to your ZIP.
                {store.contact_email && (
                  <> Email <a href={`mailto:${store.contact_email}?subject=${encodeURIComponent("Delivery exception")}`} className="underline font-semibold">{store.contact_email}</a> to ask for an exception.</>
                )}
              </div>
            )}
          </div>
          <div><Label className="text-xs">Notes (optional)</Label><Textarea value={form.delivery_notes} onChange={(e) => update("delivery_notes", e.target.value)} rows={2} placeholder="Gate code, leave on porch, etc." /></div>
          <p className="text-[11px] text-[#6B6B6B] leading-relaxed">
            Orders are non-refundable once placed. Questions? Email the seller directly.
          </p>
          <div className="rounded-lg border border-[#E8D9A6] bg-[#FBF7EC] p-3 text-[11.5px] leading-snug text-[#3B0E07]" data-testid="storefront-venue-ack-block">
            <p className="font-bold uppercase tracking-[0.18em] text-[9.5px] text-[#7A1F12] mb-1.5">
              Venue role &amp; at-own-risk
            </p>
            <p className="mb-2">
              You are buying from <strong>{store.name}</strong>, an independent
              seller. <strong>Armstrong K9 Registry is a venue</strong> — not
              the merchant of record, not the seller, not a guarantor — and
              you are purchasing at your own risk.{" "}
              <a href="/terms" target="_blank" rel="noreferrer" className="underline font-semibold">Read the Terms</a>.
            </p>
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={!!form.venue_acknowledged}
                onChange={(e) => update("venue_acknowledged", e.target.checked)}
                className="mt-0.5 h-4 w-4 accent-[#7A1F12]"
                data-testid="storefront-venue-ack-checkbox"
                required
              />
              <span>
                I acknowledge Armstrong K9 Registry is a venue and I am buying from{" "}
                {store.name} at my own risk.
              </span>
            </label>
          </div>
          {isDirect && (
            <div className="rounded-lg border-2 border-[#E5A33B]/60 bg-[#FFF8EC] p-3 text-[11.5px] leading-snug text-[#3B2A07]" data-testid="storefront-direct-payment-block">
              <p className="font-bold uppercase tracking-[0.18em] text-[9.5px] text-[#8A5A00] mb-1.5">
                International seller — direct payment
              </p>
              <p className="mb-2">
                <strong>{store.name} is located outside the United States.</strong>{" "}
                Your payment is processed directly on their own Stripe account —
                they are the merchant of record. Armstrong K9 Registry never
                holds these funds, and <strong>no Registry escrow or dispute
                protection applies</strong>. Refunds, disputes, and chargebacks
                are handled solely by the seller.
              </p>
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!form.direct_payment_ack}
                  onChange={(e) => update("direct_payment_ack", e.target.checked)}
                  className="mt-0.5 h-4 w-4 accent-[#8A5A00]"
                  data-testid="storefront-direct-payment-checkbox"
                  required
                />
                <span>
                  I understand my payment goes directly to {store.name} with no
                  Registry escrow or dispute protection.
                </span>
              </label>
            </div>
          )}
          <DialogFooter>
            <Button type="submit" disabled={busy || blocked || !form.venue_acknowledged || (isDirect && !form.direct_payment_ack)} className="rounded-full bg-gold-gradient text-ink font-semibold h-11 w-full disabled:opacity-50">
              {busy ? "Loading…" : blocked ? "Outside delivery zone" : `Pay $${(product.price_usd * form.quantity).toFixed(2)} for ${form.quantity} ${form.quantity === 1 ? "item" : "items"} →`}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Always-on "Request a custom item" block. Sits at the bottom of
 * every public storefront so buyers can describe what they want even
 * when nothing on the menu fits. Reuses the existing
 * CustomProductRequestDialog with `product={null}` — the backend
 * endpoint already supports product-less requests by leaving
 * `product_id` null.
 */
function RequestCustomItemBlock({ slug, store }) {
  const [open, setOpen] = React.useState(false);
  return (
    <>
      <section
        className="rounded-2xl border border-[#E8E0CC] bg-cream/50 p-6 sm:p-8 mt-6"
        data-testid="public-store-request-custom"
      >
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6">
          <span className="p-3 rounded-full bg-white border border-[#E8E0CC] shrink-0">
            <PaintBrush size={22} weight="duotone" className="text-gold-dark" />
          </span>
          <div className="min-w-0 flex-1">
            <h3 className="font-spectral text-xl tracking-tight">Looking for something custom?</h3>
            <p className="text-[13px] text-[#3a3a3a] mt-1 leading-relaxed">
              Tell {store.name} what you have in mind &mdash; a special bag size,
              a one-off design, a bundled order, anything. They&rsquo;ll reply with
              a mockup and a price. You only pay once you approve it.
            </p>
          </div>
          <Button
            onClick={() => setOpen(true)}
            className="rounded-full bg-ink hover:bg-crimson text-white shrink-0"
            data-testid="public-store-request-custom-btn"
          >
            <PaintBrush size={14} weight="bold" className="mr-1.5" />
            Request a custom item
          </Button>
        </div>
      </section>
      <CustomProductRequestDialog
        product={null}
        store={store}
        slug={slug}
        open={open}
        onClose={() => setOpen(false)}
      />
    </>
  );
}

